import { z } from 'zod'

/**
 * Préservation du contexte d'entrée (deep-link) à travers le mur d'authentification.
 *
 * Un utilisateur déconnecté qui clique un lien email profond
 * (`/?programId=X&tab=matching`, ou une route protégée comme `/sessions?...`)
 * passe par la page de login (email / MFA / SSO SAML). Sans ce mécanisme, le
 * path et la query d'origine sont perdus et l'utilisateur retombe sur le
 * dashboard générique.
 *
 * `capture()` enregistre l'URL d'origine (path + query) dans `sessionStorage`
 * avant la redirection vers le login. `consume()` la restitue une seule fois,
 * après l'obtention du token (login email, MFA, ou callback SSO), puis la purge.
 * `sessionStorage` survit au round-trip SAML dans le même onglet.
 *
 * Sécurité : seuls les paths relatifs internes sont restitués (anti
 * open-redirect). Le paramètre `token` n'est jamais conservé dans l'URL
 * restituée, et le paramètre interne `from` (posé par le guard) est ignoré.
 */

/** Clé `sessionStorage` du contexte d'entrée à restituer après authentification. */
export const RETURN_TO_KEY = 'auth:return_to'

/** Paramètres de query jamais conservés dans l'URL restituée. */
const STRIPPED_QUERY_PARAMS = ['token', 'from']

const internalPathSchema = z
  .string()
  .min(1)
  // Path relatif uniquement : commence par un seul `/`, jamais `//` (URL
  // protocol-relative) ni `/\` (contournement classique d'open-redirect).
  .refine((path) => path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\'))
  .refine((path) => !path.includes('\\'))
  // Résolu contre l'origine courante, le path doit rester sur la même origine.
  // Rejette `https://evil.com`, `//evil.com`, `https:/evil.com`, etc.
  .refine((path) => {
    try {
      return new URL(path, window.location.origin).origin === window.location.origin
    } catch {
      return false
    }
  })

/**
 * Normalise un path/URL candidat en path relatif interne sûr, en retirant les
 * paramètres `token` et `from`. Retourne `null` si le candidat est externe,
 * absolu, ou ne pointe pas vers du contenu applicatif réel.
 */
export function sanitizeReturnTo(raw: string | null | undefined): string | null {
  if (!raw) return null

  let url: URL
  try {
    url = new URL(raw, window.location.origin)
  } catch {
    return null
  }

  if (url.origin !== window.location.origin) return null

  STRIPPED_QUERY_PARAMS.forEach((param) => url.searchParams.delete(param))

  const search = url.searchParams.toString()
  const path = `${url.pathname}${search ? `?${search}` : ''}`

  // Pas de cible applicative réelle (racine sans query) : rien à restituer.
  if (path === '/') return null

  return internalPathSchema.safeParse(path).success ? path : null
}

/**
 * Enregistre le contexte d'entrée à restituer après authentification.
 * Sans argument, capture l'URL courante du navigateur.
 * No-op si le candidat ne porte aucune cible applicative restituable.
 */
export function captureReturnTo(raw?: string): void {
  const path = sanitizeReturnTo(raw ?? `${window.location.pathname}${window.location.search}`)
  if (!path) return

  try {
    sessionStorage.setItem(RETURN_TO_KEY, path)
  } catch {
    // sessionStorage indisponible (mode privé strict) : on ignore silencieusement.
  }
}

/** Lit le contexte d'entrée sans le purger (ré-validé). */
export function peekReturnTo(): string | null {
  try {
    return sanitizeReturnTo(sessionStorage.getItem(RETURN_TO_KEY))
  } catch {
    return null
  }
}

/**
 * Restitue le contexte d'entrée une seule fois puis le purge.
 * Retourne `null` s'il n'y a rien à restituer ou si la valeur stockée est
 * invalide (forgée vers une URL externe par exemple).
 */
export function consumeReturnTo(): string | null {
  let stored: string | null = null
  try {
    stored = sessionStorage.getItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RETURN_TO_KEY)
  } catch {
    return null
  }

  return sanitizeReturnTo(stored)
}

/** Composable exposant la capture/restitution du contexte d'entrée. */
export default function useReturnTo() {
  return {
    captureReturnTo,
    consumeReturnTo,
    peekReturnTo,
    sanitizeReturnTo,
  }
}
